import { ChatStartButton } from "@/components/chat/ChatStartButton";
import { PageContainer } from "@/components/layout/PageContainer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { normalizeImageUrl } from "@/lib/image-url";
import { apiRequest } from "@/lib/queryClient";
import {
  australianCities,
  CityClassified,
  cityClassifiedCategories,
} from "@shared/schema";
import { useQuery } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { ko } from "date-fns/locale";
import {
  AlertCircle,
  ArrowLeft,
  Calendar,
  Clock,
  Eye,
  Globe,
  Mail,
  MapPin,
  Phone,
  Tag,
  User,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useLocation, useRoute } from "wouter";

export default function CityClassifiedDetail() {
  const [match, params] = useRoute<{ id: string }>("/city-classifieds/:id");
  const { user } = useAuth();
  const { toast } = useToast();
  const [, setLocation] = useLocation();

  // Check if params is valid
  if (!match || !params) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <Card>
            <CardContent className="py-12 text-center">
              <AlertCircle className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-semibold mb-2">
                유효하지 않은 요청입니다
              </h3>
              <p className="text-muted-foreground mb-6">
                올바른 광고 ID가 필요합니다.
              </p>
              <Link href="/market">
                <Button>목록으로 돌아가기</Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const classifiedId = params.id;

  const { data: classified, isLoading } = useQuery<CityClassified>({
    queryKey: [`/api/city-classifieds/${classifiedId}`],
    enabled: !!classifiedId,
  });

  // Increment views on mount using useEffect
  // Track view count - only once per page load
  const [viewTracked, setViewTracked] = useState(false);

  useEffect(() => {
    if (!classifiedId || viewTracked) return;

    const incrementView = async () => {
      try {
        await apiRequest(
          "POST",
          `/api/city-classifieds/${classifiedId}/view`,
          {}
        );
        setViewTracked(true);
      } catch (error) {
        // Silently fail - don't show error to user for view tracking
        console.error("[CityClassifiedDetail] View tracking error:", error);
      }
    };

    incrementView();
  }, [classifiedId]); // Only run when classifiedId changes

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-muted rounded w-3/4"></div>
            <div className="h-4 bg-muted rounded w-1/2"></div>
            <div className="h-64 bg-muted rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!classified) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <Card>
            <CardContent className="py-12 text-center">
              <AlertCircle className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-semibold mb-2">
                광고를 찾을 수 없습니다
              </h3>
              <p className="text-muted-foreground mb-6">
                요청하신 광고가 존재하지 않거나 삭제되었습니다.
              </p>
              <Link href="/market">
                <Button>목록으로 돌아가기</Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const category = cityClassifiedCategories.find(
    (c) => c.id === classified.category
  );
  const cityInfo =
    classified.state && classified.state in australianCities
      ? australianCities[
          classified.state as keyof typeof australianCities
        ].find((c) => c.id === classified.city)
      : null;
  const locationLabel = [
    cityInfo?.label || classified.city,
    classified.suburb,
    classified.state,
  ]
    .filter((part): part is string => typeof part === "string" && part.trim().length > 0)
    .join(", ");
  const hasContactDetails = Boolean(
    classified.phoneNumber || classified.email || classified.websiteUrl || classified.contactMethod,
  );

  return (
    <PageContainer size="read">
      {/* Back Button */}
      <Link href="/market">
        <Button
          variant="ghost"
          className="mb-6 gap-2"
          data-testid="button-back"
        >
          <ArrowLeft className="w-4 h-4" />
          목록으로 돌아가기
        </Button>
      </Link>

      {/* Main Content Card */}
      <Card data-testid="card-classified-detail">
        <CardHeader>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <h1 className="text-3xl font-bold mb-3" data-testid="text-title">
                {classified.title}
              </h1>
              <div className="flex items-center gap-2 flex-wrap mb-2">
                {category && (
                  <Badge variant="secondary" className="gap-1">
                    <Tag className="w-3 h-3" />
                    {category.label}
                  </Badge>
                )}
                {classified.isPremium && (
                  <Badge variant="default">프리미엄</Badge>
                )}
              </div>
            </div>
          </div>

          {/* Meta Information */}
          <div className="flex items-center gap-4 text-sm text-muted-foreground mt-4 flex-wrap">
            <div className="flex items-center gap-1">
              <User className="w-4 h-4" />
              <span>{classified.userDisplayName}</span>
            </div>
            <div className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              <span>
                {formatDistanceToNow(new Date(classified.createdAt), {
                  addSuffix: true,
                  locale: ko,
                })}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Eye className="w-4 h-4" />
              <span>{classified.views}회 조회</span>
            </div>
          </div>
        </CardHeader>

        <Separator />

        <CardContent className="py-6 space-y-6">
          {/* Location */}
          <div>
            <h3 className="font-semibold mb-2 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-primary" />
              위치
            </h3>
            <p className="text-muted-foreground">
              {locationLabel || "등록된 위치 정보가 없습니다."}
            </p>
          </div>

          <Separator />

          {/* Description */}
          <div>
            <h3 className="font-semibold mb-3 text-lg">상세 설명</h3>
            <div className="prose dark:prose-invert max-w-none whitespace-pre-wrap">
              {classified.description}
            </div>
          </div>

          <Separator />

          {/* Contact Information */}
          <div>
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <Phone className="w-5 h-5 text-primary" />
              연락처
            </h3>
            <div className="space-y-2">
              {classified.phoneNumber && (
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-muted-foreground" />
                  <a
                    href={`tel:${classified.phoneNumber}`}
                    className="text-primary hover:underline"
                    data-testid="link-phone"
                  >
                    {classified.phoneNumber}
                  </a>
                </div>
              )}
              {classified.email && (
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-muted-foreground" />
                  <a
                    href={`mailto:${classified.email}`}
                    className="text-primary hover:underline"
                    data-testid="link-email"
                  >
                    {classified.email}
                  </a>
                </div>
              )}
              {classified.websiteUrl && (
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4 text-muted-foreground" />
                  <a
                    href={classified.websiteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                    data-testid="link-website"
                  >
                    {classified.websiteUrl}
                  </a>
                </div>
              )}
              {classified.contactMethod && (
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-sm text-muted-foreground">
                    선호 연락 방법:
                    {classified.contactMethod === "phone" && " 전화"}
                    {classified.contactMethod === "email" && " 이메일"}
                    {classified.contactMethod === "both" && " 전화 또는 이메일"}
                  </span>
                </div>
              )}
              {!hasContactDetails && (
                <p className="text-sm text-muted-foreground">
                  등록된 연락처 정보가 없습니다.
                </p>
              )}
            </div>
          </div>

          {/* Images */}
          {classified.images && classified.images.length > 0 && (
            <>
              <Separator />
              <div>
                <h3 className="font-semibold mb-3">사진</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {classified.images.map((image, index) => (
                    <div
                      key={index}
                      className="aspect-square rounded-lg overflow-hidden bg-muted"
                    >
                      <img
                        src={normalizeImageUrl(image) || ""}
                        alt={`${classified.title} - ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Expiry Information */}
          {classified.expiresAt && (
            <>
              <Separator />
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Clock className="w-4 h-4" />
                <span>
                  광고 만료일:{" "}
                  {new Date(classified.expiresAt).toLocaleDateString("ko-KR")}
                </span>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="mt-6 space-y-4">
        {/* Chat Button - Only show if user is not the owner */}
        {user && user.uid !== classified.userId && (
          <ChatStartButton
            listingId={String(classified.id)}
            sellerId={classified.userId || ""}
            onChatStarted={(chatId) => {
              setLocation(`/chat/${chatId}`);
            }}
            onError={(error) => {
              toast({
                description: error,
                variant: "destructive",
              });
            }}
          />
        )}

        <div className="flex gap-4">
          <Link href="/market" className="flex-1">
            <Button
              variant="outline"
              className="w-full"
              data-testid="button-back-bottom"
            >
              목록으로
            </Button>
          </Link>
          {user && user.uid === classified.userId && (
            <Button
              variant="default"
              className="flex-1"
              data-testid="button-edit"
            >
              수정하기
            </Button>
          )}
        </div>
      </div>
    </PageContainer>
  );
}
